import React from 'react';
import {Marker, OverlayView} from '@react-google-maps/api';

import MessageSvg from '../../../../assets/location/white_message.svg';
import MessageBlackSvg from '../../../../assets/location/black_message.svg';
import MessageSmallSvg from '../../../../assets/location/white_message_small.svg';
import MessageBlackSmallSvg from '../../../../assets/location/black_message_small.svg';
import MapMarkerBlackSvg from '../../../../assets/location/no_price_marker_black.svg';
import MapMarkerWhiteSvg from '../../../../assets/location/no_price_marker_white.svg';

import MapClinicInfo from './MapClinicInfo';
import Map from '../../../../components/Map';
import {formatEstimatedPrice, getCurrencyCode} from '../../../../util/utility';
import { connect } from 'react-redux';
import { compose } from 'redux';

interface LatLngInterface {
  lat: number;
  lng: number;
}

interface PriceInterface {
  Value: number;
}

interface ItemInterface {
  latitude: number;
  longitude: number;
  code?: number;
  price: PriceInterface;
  name: string;
  address: string;
  postalCode: string;
  city: string;
  phone: number;
}

interface OffsetInterface {
  x: number;
  y: number;
}

interface GenericMapWithMarkerProps {
  onLoad(map: google.maps.Map): void;
  locations: ItemInterface[];
  labels: any;
  showPrice?: boolean;
  infoBoxPosition: LatLngInterface | null;
  containerStyleClassName: string;
  zoom: number;
  dynamicZoom?: boolean;
  currentMap: google.maps.Map | null;
  withDirections?: boolean;
  offset: OffsetInterface;
  buStyles: any;
  precoEstimadoFuncAtiva: boolean;
  enablePriceMarker: boolean;
  handleChangeInfoBoxPosition(value: LatLngInterface | null): void;
  handleMarkerClick(index: number, latitude: number, longitude: number): void;
  onClickLocationDetails?(item: ItemInterface): void;
}

const GenericMapWithMarker = ({
  offset,
  onLoad,
  locations,
  labels,
  showPrice,
  infoBoxPosition,
  zoom,
  dynamicZoom = false,
  precoEstimadoFuncAtiva,
  currentMap,
  handleMarkerClick,
  handleChangeInfoBoxPosition,
  onClickLocationDetails,
  enablePriceMarker,
  containerStyleClassName,
  withDirections,
  buStyles,
}: GenericMapWithMarkerProps) => {
  const shouldRenderInfoWindow = (item: ItemInterface) => {
    return (
      infoBoxPosition?.lat &&
      infoBoxPosition?.lng &&
      infoBoxPosition.lat === item.latitude &&
      infoBoxPosition.lng === item.longitude
    );
  };

  const getIconUrl = (item: ItemInterface) => {
    if (showPrice && precoEstimadoFuncAtiva && enablePriceMarker) {
      const isValueBig =
        item?.price?.Value && item.price.Value.toString().length > 2;
      if (shouldRenderInfoWindow(item)) {
        return isValueBig ? MessageBlackSvg : MessageBlackSmallSvg;
      } else {
        return isValueBig ? MessageSvg : MessageSmallSvg;
      }
    } else {
      if (shouldRenderInfoWindow(item)) {
        return MapMarkerBlackSvg;
      } else {
        return MapMarkerWhiteSvg;
      }
    }
  };

  const getIconLabel = (item: ItemInterface) => {
    return showPrice && precoEstimadoFuncAtiva && enablePriceMarker
      ? {
          fontSize: '11px',
          fontFamily: `'Poppins', sans-serif`,
          text: item?.price?.Value
            ? `${formatEstimatedPrice(
                item.price.Value.toString()
              )}${getCurrencyCode(undefined, "rede")}`
            : '-',
          fontWeight: 'bold',
          color: shouldRenderInfoWindow(item) ? '#fff' : '#000',
        }
      : '';
  };

  const handleClose = () => {
    handleChangeInfoBoxPosition(null);
  };

  React.useEffect(() => {
    if (currentMap && locations?.length) {
      google.maps.event.addListenerOnce(
        currentMap,
        'bounds_changed',
        function () {
          !dynamicZoom && currentMap.setZoom(zoom);
        }
      );
      const bounds = new window.google.maps.LatLngBounds();
      locations.map(
        (location: ItemInterface) =>
          !!(location.latitude && location.longitude) &&
          bounds.extend(
            new window.google.maps.LatLng(location.latitude, location.longitude)
          )
      );
      currentMap.fitBounds(bounds);
    }
  }, [currentMap, locations, zoom]);

  return (
    <Map
      zoom={zoom}
      containerStyleClassName={containerStyleClassName}
      onLoad={onLoad}>
      {locations.map((item: ItemInterface, index: number) => (
        <Marker
          icon={{
            url: getIconUrl(item),
            labelOrigin: new google.maps.Point(
              item?.price?.Value && item.price.Value.toString().length > 2
                ? 29
                : 18,
              16
            ),
          }}
          label={getIconLabel(item)}
          position={{lat: item.latitude, lng: item.longitude}}
          onClick={() =>
            handleMarkerClick(index, item.latitude, item.longitude)
          }>
          {shouldRenderInfoWindow(item) && (
            <OverlayView
              position={{lat: item.latitude, lng: item.longitude}}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              getPixelPositionOffset={() => offset}>
              <MapClinicInfo
                buStyles={buStyles}
                handleClose={handleClose}
                handleOnClickLocationDetails={onClickLocationDetails}
                item={item}
                withDirections={withDirections}
              />
            </OverlayView>
          )}
        </Marker>
      ))}
    </Map>
  );
};

const mapStateToProps = ({strings}: any) => ({
  labels: strings.value.pt.rede,
});

export default compose(connect(mapStateToProps)(GenericMapWithMarker));